import * as React from 'react';

import { DataSourceCache } from './cache';
import { DataModel, DataSource } from './types';

function useCachedDataSource<D extends DataModel>(
  dataSource: DataSource<D>,
  cache: DataSourceCache | null
): DataSource<D> {
  return React.useMemo(() => {
    if (!cache) {
      return dataSource;
    }

    const { getOne, getMany, createOne, updateOne, deleteOne, ...rest } =
      dataSource;

    return {
      ...Object.fromEntries(
        Object.entries({ getOne, getMany })
          .filter(([_key, method]) => !!method)
          .map(([key, method]) => [
            key,
            async (...args: unknown[]) => {
              const cacheKey = JSON.stringify([key, ...args]);

              const cacheValue = cache.get(cacheKey);

              if (cacheValue) {
                return cacheValue;
              }

              const result = await (
                method as (
                  ...args: unknown[]
                ) =>
                  | ReturnType<NonNullable<DataSource<D>['getOne']>>
                  | ReturnType<NonNullable<DataSource<D>['getMany']>>
              )(...args);

              cache.set(cacheKey, result);
              return result;
            },
          ])
      ),
      ...Object.fromEntries(
        Object.entries({ createOne, updateOne, deleteOne })
          .filter(([_key, method]) => !!method)
          .map(([key, method]) => [
            key,
            async (...args: unknown[]) => {
              const result = await (
                method as (
                  ...args: unknown[]
                ) =>
                  | ReturnType<NonNullable<DataSource<D>['createOne']>>
                  | ReturnType<NonNullable<DataSource<D>['updateOne']>>
                  | ReturnType<NonNullable<DataSource<D>['deleteOne']>>
              )(...args);

              cache.clear();
              return result;
            },
          ])
      ),
      ...rest,
    };
  }, [cache, dataSource]);
}

export { useCachedDataSource };
